import React from "react";
import { GitBranch, Trash2, X, PlusCircle } from "lucide-react";

const ConditionalLogicSection = ({
    conditions,
    options,
    formKey,
    colors,
    inputStyle,
    onSetCond,
    onSetCondRule,
    onAddCondRule,
    onRemoveCondRule,
    onAddCondGroup,
    onRemoveCondGroup,
}) => (
    <div style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}`, padding: '20px 24px' }}>
        <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center gap-2">
                <GitBranch size={16} color="#C5A075" />
                <span style={{ fontWeight: '700', fontSize: '0.9rem', color: colors?.textMain || '#fff' }}>Conditional Logic</span>
                <span style={{ fontSize: '0.72rem', color: colors?.textMuted || '#8B7D6B', fontWeight: '500' }}>
                    — show this option only when conditions are met
                </span>
            </div>
            <div
                className="d-flex align-items-center gap-2"
                style={{ cursor: 'pointer' }}
                onClick={() => onSetCond('enabled', !conditions?.enabled)}
            >
                <div style={{ width: '36px', height: '20px', borderRadius: '20px', backgroundColor: conditions?.enabled ? '#C5A075' : '#D1D5DB', position: 'relative', transition: 'background-color 0.2s', display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0 }}>
                    <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff', transform: conditions?.enabled ? 'translateX(16px)' : 'translateX(0)', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
                <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors?.textMain || '#fff', userSelect: 'none' }}>
                    {conditions?.enabled ? 'ON' : 'OFF'}
                </span>
            </div>
        </div>

        {conditions?.enabled && (
            <div style={{ marginTop: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}` }}>
                        {[{ val: 'show', label: 'Show' }, { val: 'hide', label: 'Hide' }].map(opt => (
                            <button key={opt.val} type="button"
                                onClick={() => onSetCond('action', opt.val)}
                                style={{ padding: '4px 14px', fontSize: '0.78rem', fontWeight: '700', border: 'none', cursor: 'pointer', backgroundColor: (conditions?.action || 'show') === opt.val ? '#C5A075' : 'transparent', color: (conditions?.action || 'show') === opt.val ? '#fff' : (colors?.textMuted || '#8B7D6B'), transition: 'all 0.15s' }}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: colors?.textMuted || '#8B7D6B', fontWeight: '500' }}>this option when</span>
                    <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}` }}>
                        {[{ val: 'and', label: 'ALL' }, { val: 'or', label: 'ANY' }].map(opt => (
                            <button key={opt.val} type="button"
                                onClick={() => onSetCond('groupLogic', opt.val)}
                                style={{ padding: '4px 14px', fontSize: '0.78rem', fontWeight: '700', border: 'none', cursor: 'pointer', backgroundColor: (conditions?.groupLogic || 'and') === opt.val ? '#C5A075' : 'transparent', color: (conditions?.groupLogic || 'and') === opt.val ? '#fff' : (colors?.textMuted || '#8B7D6B'), transition: 'all 0.15s' }}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: colors?.textMuted || '#8B7D6B', fontWeight: '500' }}>
                        {(conditions?.groupLogic || 'and') === 'and' ? 'groups match:' : 'group matches:'}
                    </span>
                </div>

                {(conditions?.groups || []).map((group, gIdx) => (
                    <React.Fragment key={gIdx}>
                        {gIdx > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '10px 0' }}>
                                <div style={{ flex: 1, height: '1px', backgroundColor: colors?.borderColor || '#E9E4DB' }} />
                                <span style={{ fontSize: '0.7rem', fontWeight: '800', letterSpacing: '1.2px', color: '#C5A075', padding: '2px 12px', borderRadius: '20px', border: '1.5px solid #C5A075', backgroundColor: 'rgba(197,160,117,0.08)' }}>AND</span>
                                <div style={{ flex: 1, height: '1px', backgroundColor: colors?.borderColor || '#E9E4DB' }} />
                            </div>
                        )}

                        <div style={{ borderRadius: '12px', border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}`, padding: '12px 14px', backgroundColor: 'rgba(197,160,117,0.03)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px', color: colors?.textMuted || '#8B7D6B', textTransform: 'uppercase' }}>
                                    Group {gIdx + 1}
                                </span>
                                {(conditions?.groups || []).length > 1 && (
                                    <button type="button" onClick={() => onRemoveCondGroup(gIdx)}
                                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px 6px', borderRadius: '6px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Trash2 size={13} /> Remove group
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(group.rules || []).length === 0 && (
                                    <div style={{ fontSize: '0.78rem', color: colors?.textMuted || '#8B7D6B', fontStyle: 'italic', padding: '4px 0 8px' }}>
                                        No rules yet — add one below.
                                    </div>
                                )}
                                {(group.rules || []).map((rule, rIdx) => {
                                    const refOption = options.find(o => o.key === rule.optionKey);
                                    const refChoices = refOption?.choices || [];
                                    const isTextType = refOption?.type === 'custom-text';
                                    return (
                                        <React.Fragment key={rIdx}>
                                            {rIdx > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '2px 0' }}>
                                                    <div style={{ flex: 1, height: '1px', backgroundColor: colors?.borderColor || '#E9E4DB', opacity: 0.5 }} />
                                                    <span style={{ fontSize: '0.66rem', fontWeight: '800', letterSpacing: '0.8px', color: colors?.textMuted || '#8B7D6B' }}>OR</span>
                                                    <div style={{ flex: 1, height: '1px', backgroundColor: colors?.borderColor || '#E9E4DB', opacity: 0.5 }} />
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <select value={rule.optionKey}
                                                    onChange={e => onSetCondRule(gIdx, rIdx, 'optionKey', e.target.value)}
                                                    style={{ ...inputStyle, flex: '2 1 0', fontSize: '0.8rem', padding: '5px 8px', borderRadius: '8px', cursor: 'pointer' }}>
                                                    <option value="">— Select option —</option>
                                                    {options.filter(o => o.key !== formKey).map(o => (
                                                        <option key={o._id} value={o.key}>{o.label}</option>
                                                    ))}
                                                </select>
                                                <select value={rule.operator}
                                                    onChange={e => onSetCondRule(gIdx, rIdx, 'operator', e.target.value)}
                                                    style={{ ...inputStyle, flex: '0 0 auto', width: '110px', fontSize: '0.8rem', padding: '5px 8px', borderRadius: '8px', cursor: 'pointer' }}>
                                                    <option value="is">is</option>
                                                    <option value="is_not">is not</option>
                                                    <option value="contains">contains</option>
                                                    <option value="not_contains">not contains</option>
                                                </select>
                                                {isTextType || refChoices.length === 0 ? (
                                                    <input type="text" value={rule.value}
                                                        onChange={e => onSetCondRule(gIdx, rIdx, 'value', e.target.value)}
                                                        placeholder="value..."
                                                        style={{ ...inputStyle, flex: '2 1 0', fontSize: '0.8rem', padding: '5px 8px', borderRadius: '8px' }} />
                                                ) : (
                                                    <select value={rule.value}
                                                        onChange={e => onSetCondRule(gIdx, rIdx, 'value', e.target.value)}
                                                        style={{ ...inputStyle, flex: '2 1 0', fontSize: '0.8rem', padding: '5px 8px', borderRadius: '8px', cursor: 'pointer' }}>
                                                        <option value="">— Select value —</option>
                                                        {refChoices.map(c => (
                                                            <option key={c.value} value={c.value}>{c.label}</option>
                                                        ))}
                                                    </select>
                                                )}
                                                <button type="button" onClick={() => onRemoveCondRule(gIdx, rIdx)}
                                                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px', flexShrink: 0 }}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            <button type="button" onClick={() => onAddCondRule(gIdx)}
                                style={{ marginTop: '8px', border: `1px dashed ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '8px', color: colors?.textMuted || '#8B7D6B', backgroundColor: 'transparent', fontSize: '0.75rem', padding: '3px 12px', cursor: 'pointer' }}>
                                <PlusCircle size={13} /> Add Rule (OR)
                            </button>
                        </div>
                    </React.Fragment>
                ))}

                <button type="button" onClick={onAddCondGroup}
                    style={{ marginTop: '10px', border: '1.5px dashed #C5A075', borderRadius: '8px', color: '#C5A075', backgroundColor: 'transparent', fontSize: '0.8rem', padding: '5px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                    <PlusCircle size={14} /> Add Group (AND)
                </button>
            </div>
        )}
    </div>
);

export default ConditionalLogicSection;
